/**
 * WorkState Manager
 * Automatically saves and restores work state to prevent data loss
 */

class WorkstateManager {
  constructor() {
    this.autoSaveInterval = null;
    this.saveInterval = 30000; // Save every 30 seconds
    this.maxBackups = 5; // Keep maximum 5 backups
    this.storageKey = 'dnd_map_workstate';
    this.backupPrefix = 'dnd_map_backup_';
  }

  /**
   * Set save interval
   */
  setSaveInterval(intervalSeconds) {
    this.saveInterval = Math.max(5000, intervalSeconds * 1000); // Minimum 5 seconds
  }

  /**
   * Start automatic saving
   */
  startAutoSave(getStateCallback, intervalSeconds = 30) {
    // Update interval if provided
    if (intervalSeconds) {
      this.setSaveInterval(intervalSeconds);
    }

    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }

    this.autoSaveInterval = setInterval(async () => {
      try {
        if (typeof getStateCallback === 'function') {
          const state = await getStateCallback();
          if (state) {
            this.saveWorkState(state);
          }
        }
               } catch (error) {
           // Silent failure for auto-save
         }
    }, this.saveInterval);


  }

  /**
   * Stop automatic saving
   */
  stopAutoSave() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;

    }
  }

  /**
   * Save current work state
   */
  saveWorkState(gameState, metadata = {}) {
    try {
      const workState = {
        gameState,
        metadata: {
          ...metadata,
          savedAt: new Date().toISOString(),
          url: window.location.href,
          userId: metadata.userId || 'anonymous'
        }
      };

      // Create backup before saving new state
      this.createBackup();

      // Save current state
      localStorage.setItem(this.storageKey, JSON.stringify(workState));
      

      return true;
    } catch (error) {
      // Silent failure for work state saving
      return false;
    }
  }

  /**
   * Get saved work state
   */
  getWorkState() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (saved) {
        const workState = JSON.parse(saved);
        
        // Check if it's recent (within last 24 hours)
        if (workState.metadata?.savedAt) {
          const savedTime = new Date(workState.metadata.savedAt);
          const now = new Date();
          const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
          
          if (hoursDiff < 24) {
            return workState;
          } else {
            this.clearWorkState();
          }
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if there's unsaved work
   */
  hasUnsavedWork() {
    const workState = this.getWorkState();
    return workState !== null;
  }

  /**
   * Get work state info for display
   */
  getWorkStateInfo() {
    const workState = this.getWorkState();
    if (!workState) return null;

    return {
      savedAt: workState.metadata.savedAt,
      layerCount: workState.gameState?.layers?.length || 0,
      containerCount: workState.gameState?.containers?.length || 0,
      tokenCount: workState.gameState?.tokens?.length || 0,
      gameName: workState.gameState?.metadata?.name || 'Untitled Game'
    };
  }

  /**
   * Clear work state after successful save/load
   */
  clearWorkState() {
    localStorage.removeItem(this.storageKey);

  }

  /**
   * Create backup of current state
   */
  createBackup() {
    try {
      const current = localStorage.getItem(this.storageKey);
      if (!current) return;

      const timestamp = Date.now();
      const backupKey = `${this.backupPrefix}${timestamp}`;
      
      localStorage.setItem(backupKey, current);
      
      // Clean old backups
      this.cleanOldBackups();
    } catch (error) {
      // Silent failure for backup creation
    }
  }

  /**
   * Clean old backup files
   */
  cleanOldBackups() {
    try {
      const backupKeys = [];
      
      // Find all backup keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.backupPrefix)) {
          backupKeys.push(key);
        }
      }

      // Sort by timestamp (newest first)
      backupKeys.sort((a, b) => {
        const timeA = parseInt(a.replace(this.backupPrefix, ''));
        const timeB = parseInt(b.replace(this.backupPrefix, ''));
        return timeB - timeA;
      });

      // Remove old backups (keep only maxBackups)
      for (let i = this.maxBackups; i < backupKeys.length; i++) {
        localStorage.removeItem(backupKeys[i]);
      }
    } catch (error) {
      // Silent failure for backup cleanup
    }
  }

  /**
   * Get available backups
   */
  getBackups() {
    try {
      const backups = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.backupPrefix)) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            const timestamp = parseInt(key.replace(this.backupPrefix, ''));
            
            backups.push({
              key,
              timestamp,
              savedAt: new Date(timestamp).toISOString(),
              metadata: data.metadata
            });
          } catch (e) {
            // Skip invalid backup
          }
        }
      }

      // Sort by timestamp (newest first)
      return backups.sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
      return [];
    }
  }

  /**
   * Restore from backup
   */
  restoreFromBackup(backupKey) {
    try {
      const backupData = localStorage.getItem(backupKey);
      if (!backupData) {
        throw new Error('Backup not found');
      }

      const workState = JSON.parse(backupData);
      
      // Save as current work state
      localStorage.setItem(this.storageKey, backupData);
      
      return workState;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Export work state to file
   */
  exportWorkState() {
    try {
      const workState = this.getWorkState();
      if (!workState) {
        throw new Error('No work state to export');
      }

      const filename = `dnd_map_backup_${new Date().toISOString().split('T')[0]}.json`;
      const blob = new Blob([JSON.stringify(workState, null, 2)], 
        { type: 'application/json' });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Import work state from file
   */
  async importWorkState(file) {
    try {
      const text = await file.text();
      const workState = JSON.parse(text);
      
      // Validate structure
      if (!workState.gameState || !workState.metadata) {
        throw new Error('Invalid work state file format');
      }

      // Save as current work state
      localStorage.setItem(this.storageKey, text);
      
      return workState;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get storage usage info
   */
  getStorageInfo() {
    try {
      let totalSize = 0;
      let workStateSize = 0;
      let backupSize = 0;
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const value = localStorage.getItem(key);
        const size = value.length;
        
        totalSize += size;
        
        if (key === this.storageKey) {
          workStateSize = size;
        } else if (key.startsWith(this.backupPrefix)) {
          backupSize += size;
        }
      }

      return {
        totalSize: Math.round(totalSize / 1024), // KB
        workStateSize: Math.round(workStateSize / 1024), // KB
        backupSize: Math.round(backupSize / 1024), // KB
        backupCount: this.getBackups().length
      };
    } catch (error) {
      return null;
    }
  }
}

// Create singleton instance
const workstateManager = new WorkstateManager();

export default workstateManager; 